import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  validateEmail,
  validatePassword,
  validateField,
  validateMultipleFields,
  isFormValid,
  getFirstErrorField,
  decryptResponse,
  setSessionKey,
  encryptResponse
} from '../../services';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useDispatch } from 'react-redux';
import { clearToken, clearUserDetails, setToken, setUserDetails } from '../../features/auth/authSlice';
import OtpInput from '../../components/common/OtpInput';

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [onValueChange, setOnValueChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(clearToken());
    dispatch(clearUserDetails());
  }, []);

  const handleOtpPopupClose = () => {
    setIsOtpPopupOpen(false);
    setOtp('');
    setOtpError('');
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
        setOtpError('Please enter the 6-digit OTP.');
        return;
    }
    const payload = {
        request_id: 'test_otp',
        action: "verify_otp",
        data: await encryptResponse({
            email_id: email,
            otp: otp
        }, REQ_ENC_KEY)
    };
    try {
        const response = await httpService.post<any>(`${API_BASE_URL}/user/verifyOTP`, payload);
        if (response.data.status === 200 && response.data.data) {
            const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
            const is_email_verified = (decryptedData as any).is_email_verified;
            setOtpError('');
            const loginPayload = {
              request_id: "login_req",
              timestamp: new Date().toISOString(),
              action: "organization_login",
              data: await encryptResponse({
                email_id: email,
                password: password,
                is_email_verified: is_email_verified
              }, REQ_ENC_KEY)
            };
            type LoginResponse = { status: number;[key: string]: any };
            const loginResponse = await httpService.post<LoginResponse>(`${API_BASE_URL}/user/login`, loginPayload);
            if (loginResponse.data.status === 200 && loginResponse.data.data) {
              setErrors({
                email: '',
                password: ''
              });
              setApiError('');
              const decryptedLoginData = await decryptResponse(loginResponse.data.data, REQ_ENC_KEY);
              const { session_key, payload } = (decryptedLoginData as any);
              // Keep the session key in memory only; it decrypts this and
              // subsequent responses for the session.
              setSessionKey(session_key);
              const decrypted = await decryptResponse<{
                token: string;
                refresh_token?: string;
                userDetails: any;
                user_process: any;
                activePlanDetails: any;
              }>(payload, session_key);
              const { token, userDetails, user_process, activePlanDetails } = decrypted;
              dispatch(setToken(token));
              dispatch(setUserDetails({ ...userDetails, ...user_process, activePlanDetails }));
              toast.success('Login Successful!', { position: 'top-right', autoClose: 1500 });
              navigate('/home');
              setLoading(false);
            } else {
              setLoading(false);
              setApiError('Login failed. Please check your credentials.');
            }
            setIsOtpPopupOpen(false);
            // setIsEmailVerified(response.data.data.is_email_verified);
            // toast.success('Email verified successfully!', { position: 'top-right', autoClose: 1500 });
        } else {
            setOtpError('Invalid OTP. Please try again.');
        }
    } catch (error) {
        console.log(error);
        setOtpError('An error occurred while verifying OTP.');
    }
};

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnValueChange(true);
    setLoading(false);
    const value = e.target.value;
    setEmail(value);
    setApiError('');

    if (touched.email) {
      const error = validateEmail(value);
      setErrors({
        ...errors,
        email: error
      });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnValueChange(true);
    setLoading(false);
    const value = e.target.value;
    setPassword(value);
    setApiError('');

    if (touched.password) {
      const error = validatePassword(value);
      setErrors({
        ...errors,
        password: error
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onValueChange) return;
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    const error = validateField(name, value);
    setErrors({
      ...errors,
      [name]: error
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (loading) return;
    e.preventDefault();
    setTouched({
      email: true,
      password: true
    });
    const validationResults = validateMultipleFields({
      email,
      password
    });

    const newErrors = {
      email: validationResults.email || '',
      password: validationResults.password || ''
    };

    setErrors(newErrors);
    if (!isFormValid(newErrors)) {
      const firstErrorField = getFirstErrorField(newErrors);
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        element?.focus();
      }
      return;
    }
    setLoading(true);
    setIsLoading(true);
    const payload = {
      request_id: 'signup_otp',
      action: "send_otp",
      data: await encryptResponse({
        email_id: email,
        purpose: "login",
        password: password
      }, REQ_ENC_KEY)
    };
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
      if (response.data.status === 200) {
        setIsOtpPopupOpen(true);
        toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
      } else {
        const errorMessage = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        toast.error(errorMessage, { position: 'top-right', autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while sending OTP.', { position: 'top-right', autoClose: 1500 });
    } finally {
      setIsLoading(false); // Stop loading
    }
    // const loginPayload = {
    //   request_id: "login_req",
    //   timestamp: new Date().toISOString(),
    //   action: "organization_login",
    //   data: {
    //     email_id: email,
    //     password: password
    //   }
    // };
    // type LoginResponse = { status: number;[key: string]: any };
    // const loginResponse = await httpService.post<LoginResponse>(`${API_BASE_URL}/user/login`, loginPayload);
    // if (loginResponse.data.status === 200) {
    //   setErrors({
    //     email: '',
    //     password: ''
    //   });
    //   setApiError('');
    //   const { token, userDetails, user_process, activePlanDetails } = loginResponse.data.data;
    //   dispatch(setToken(token));
    //   dispatch(setUserDetails({ ...userDetails, ...user_process, activePlanDetails }));
    //   toast.success('Login Successful!', { position: 'top-right', autoClose: 1500 });
    //   navigate('/home');
    //   setLoading(false);
    // } else {
    //   setLoading(false);
    //   setApiError('Login failed. Please check your credentials.');
    // }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth={false} sx={{ height: '100vh', py: 6 }}>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Sending OTP...
          </Typography>
        </Box>
      </Backdrop>
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          left: 24,
          zIndex: 1000,
        }}
      >
        <img
          src="/images/sampark-logo.png"
          alt="Company Logo"
          style={{ height: '80px', width: 'auto' }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Paper
          elevation={6}
          sx={{
            width: { xs: '95%', md: '90%', lg: '80%' },
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            minHeight: 450
          }}
        >
          {/* Left decorative panel */}
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #0d6fbf 0%, #0467b4 50%, #0066cc 100%)',
              color: '#fff',
              p: 6,
              position: 'relative',
              display: { xs: 'none', md: 'block' }
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, letterSpacing: 1 }}>
              WELCOME
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Sign in to access your Sampark workspace
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 460, }}>
              Sampark empowers your communication with secure WebRTC calling and real-time chat solutions simple, fast, and reliable.
            </Typography>

            {/* Decorative circles */}
            <Box sx={{ position: 'absolute', bottom: -40, left: -40 }}>
              <Box sx={{ width: 140, height: 140, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '50%', boxShadow: 6 }} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 40, left: 60 }}>
              <Box sx={{ width: 100, height: 100, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '50%', boxShadow: 6 }} />
            </Box>
            <Box sx={{ position: 'absolute', top: -50, right: -50 }}>
              <Box sx={{ width: 180, height: 180, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '50%', boxShadow: 6 }} />
            </Box>
            <Box
              component="img"
              src="/images/signupVector.png"
              alt="Signup Illustration"
              sx={{
                width: '100%',
                height: '300px',
                marginTop: '25px',
                borderRadius: 2
              }}
            />
          </Box>

          {/* Right form panel */}
          <Box sx={{ flex: 1, p: { xs: 4, md: 6 }, bgcolor: '#fff', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', height: '470px' }}>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#0b2b4a' }}>
                Sign in
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                Sign in to access Sampark’s WebRTC and real-time chat services
              </Typography>

              {apiError && (
                <Typography variant="body2" sx={{ color: 'red', textAlign: 'center', mb: 2 }}>
                  {apiError}
                </Typography>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="email"
                  label="User Name"
                  name="email"
                  autoComplete="off"
                  autoFocus
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#9aa4b2' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ bgcolor: '#ffffff', borderRadius: 1 }}
                />

                <TextField
                  margin="dense"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="off"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#9aa4b2' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Link component="button" type="button" onClick={handleClickShowPassword} sx={{ fontSize: 12, marginTop: 1 }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </Link>
                      </InputAdornment>
                    )
                  }}
                  sx={{ bgcolor: '#ffffff', borderRadius: 1 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                  <Link type="button" variant="body2" onClick={() => navigate('/forgot-password')} sx={{ textDecoration: 'none' }}>
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={
                    !email ||
                    !password ||
                    loading ||
                    Object.values(errors).some(error => error !== '')
                  }
                  sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: '#0b4f88', '&:hover': { bgcolor: '#093f6c' }, borderRadius: 2 }}
                >
                  Sign in
                </Button>

                <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 2 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="body2" sx={{ color: '#9aa4b2' }}>Or</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ display: 'inline', color: '#6b7280' }}>Don't have an account? </Typography>
                <Link component="button" variant="body2" onClick={() => navigate('/signup')} sx={{ textDecoration: 'none', marginLeft: '5px' }}>
                  Sign Up
                </Link>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#9aa4b2', fontSize: '0.875rem' }}>
                  By signing in, you agree to our{' '}
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => navigate('/terms')}
                    sx={{ 
                      textDecoration: 'none', 
                      color: '#0b4f88',
                      fontSize: '0.875rem',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Terms and Conditions
                  </Link>
                  {' '}and{' '}
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => navigate('/privacy-policy')}
                    sx={{ 
                      textDecoration: 'none', 
                      color: '#0b4f88',
                      fontSize: '0.875rem',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Dialog
        open={isOtpPopupOpen}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: '500px',
              maxWidth: '90vw'
            }
          }
        }}
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Enter the 6-digit OTP sent to <strong>{email}</strong>.
          </Typography>
          <OtpInput
            id="otp"
            value={otp}
            onChange={(value) => {
              setOtp(value);
              if (otpError) {
                setOtpError('');
              }
            }}
            error={!!otpError}
            helperText={otpError}
            autoFocus={isOtpPopupOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpPopupClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={verifyOtp} color="primary" disabled={otp.length !== 6}>
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
